import moment from 'moment';
import XLSX from 'sheetjs-style';
import { CompteEntity } from '../../../../entities/compte.entity';
import { GrandLivreEntity } from '../../../../entities/grandLivre.entity';
import { SECONDARY } from '../../../../shared/colors';

const countColumns = (data: any[]) => {
  let firstLine = data[0];
  return Object.keys(firstLine).length;
};

const tableHeaderStyles = {
  font: {
    name: 'Poppins-B',
    sz: 10,
    color: { rgb: 'FFFFFF' },
  },
  fill: {
    bgColor: SECONDARY,
  },
  border: {
    top: { style: 'thin', color: { rgb: 'FFFFFF' } },
    bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
    left: { style: 'thin', color: { rgb: 'FFFFFF' } },
    right: { style: 'thin', color: { rgb: 'FFFFFF' } },
  },
};

const tableContentStyles = {
  font: {
    name: 'Poppins-L',
    sz: 10,
  },
  border: {
    top: { style: 'thin' },
    bottom: { style: 'thin' },
    left: { style: 'thin' },
    right: { style: 'thin' },
  },
};

export const generateGLToExport = (GL: GrandLivreEntity[]) => {
  let toExport: any[] = [];
  let index = 1;
  for (let gl of GL) {
    toExport.push({
      Index: index,
      'Numero Compte': gl.num_compte,
      'Intitulé Compte': gl.intitule_compte,
      'Date saisi': gl.date_saisi,
      'Date document': gl.date_document,
      Document: gl.document,
      'Pièce comptable': gl.piece,
      'Libellé transaction': gl.libelle,
      'Catégorie titre': gl.categorie_titre,
      Emetteur: gl.tresor_emetteur,
      Titulaire: gl.titulaire,
      Crédit: gl.credit,
      Débit: gl.debit,
      Solde: gl.solde,
      'Actif (O/N)': gl.actif,
      Type: gl.type,
      'Utilisateur saisi': gl.utilisateur_saisi?.split('/')[5] || '',
      'Utilisateur validé': gl.utilisateur_valide?.split('/')[5] || '',
      'Date création': `${moment(gl.created_at).format('DD/MM/YYYY HH:mm:ss')}`,
      'Date modification': `${moment(gl.updated_at).format(
        'DD/MM/YYYY HH:mm:ss',
      )}`,
    });
    index += 1;
  }

  return toExport;
};

export const exportGLData = (GL: GrandLivreEntity[]) => {
  let wb = XLSX.utils.book_new();
  const glData = generateGLToExport(GL);
  let ws1 = XLSX.utils.json_to_sheet(glData);

  // style cells
  let numberCols = countColumns(glData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws1[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= GL.length + 1; j++) {
      ws1[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }

  XLSX.utils.book_append_sheet(wb, ws1, 'Grand livre');
  XLSX.writeFile(
    wb,
    `Grand livre - ${moment(new Date()).format('DD/MM/YYYY')}.xlsx`,
  );
};

export const generateCompteToExport = (comptes: CompteEntity[]) => {
  let toExport: any[] = [];
  let index = 1;
  for (let cpt of comptes) {
    toExport.push({
      Index: index,
      'Code chapitre': cpt.code_chapitre,
      'Code titre': cpt.code_titre,
      'Code SVT': cpt.code_svt,
      'Code client': cpt.code_client,
      'Code agence': cpt.code_agence,
      'Numero Compte': cpt.num_compte,
      'Intitulé Compte': cpt.intitule,
      Crédit: cpt.credit,
      Débit: cpt.debit,
      Solde: cpt.solde,
      'Date dernier crédit': cpt.date_dernier_credit
        ? `${moment(cpt.date_dernier_credit).format('DD/MM/YYYY HH:mm:ss')}`
        : '',
      'Date dernier dédit': cpt.date_dernier_debit
        ? `${moment(cpt.date_dernier_debit).format('DD/MM/YYYY HH:mm:ss')}`
        : '',
      'Statut (O/N)': cpt.statut,
      'Date création': `${moment(cpt.created_at).format(
        'DD/MM/YYYY HH:mm:ss',
      )}`,
      'Date modification': `${moment(cpt.updated_at).format(
        'DD/MM/YYYY HH:mm:ss',
      )}`,
    });
    index += 1;
  }

  return toExport;
};

export const exportCompteData = (comptes: CompteEntity[]) => {
  let wb = XLSX.utils.book_new();
  const compteData = generateCompteToExport(comptes);
  let ws1 = XLSX.utils.json_to_sheet(compteData);

  // style cells
  let numberCols = countColumns(compteData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws1[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= comptes.length + 1; j++) {
      ws1[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }

  XLSX.utils.book_append_sheet(wb, ws1, 'Comptes');
  XLSX.writeFile(
    wb,
    `Comptes - ${moment(new Date()).format('DD/MM/YYYY')}.xlsx`,
  );
};

export const exportBGData = (BG: any) => {
  let wb = XLSX.utils.book_new();
  const bgGeneralData = BG.globale;
  const bgTitreData = BG.titre;
  const bgTresorData = BG.tresor;
  const bgTitulaireData = BG.titulaire;
  // définir les feuilles
  let ws1 = XLSX.utils.json_to_sheet(bgGeneralData);
  let ws2 = XLSX.utils.json_to_sheet(bgTitreData);
  let ws3 = XLSX.utils.json_to_sheet(bgTresorData);
  let ws4 = XLSX.utils.json_to_sheet(bgTitulaireData);

  // style cells
  let numberCols = countColumns(bgGeneralData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws1[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= bgGeneralData.length + 1; j++) {
      ws1[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }
  numberCols = countColumns(bgTitreData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws2[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= bgTitreData.length + 1; j++) {
      ws2[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }
  numberCols = countColumns(bgTresorData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws3[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= bgTresorData.length + 1; j++) {
      ws3[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }
  numberCols = countColumns(bgTitulaireData);
  for (let i = 0; i < numberCols; i++) {
    // style table header
    ws4[`${String.fromCharCode(65 + i)}1`].s = tableHeaderStyles;
    // style content of table
    for (let j = 2; j <= bgTitulaireData.length + 1; j++) {
      ws4[`${String.fromCharCode(65 + i)}${j}`].s = tableContentStyles;
    }
  }

  XLSX.utils.book_append_sheet(wb, ws1, 'BG générale');
  XLSX.utils.book_append_sheet(wb, ws2, 'BG par titre');
  XLSX.utils.book_append_sheet(wb, ws3, 'BG par trésor');
  XLSX.utils.book_append_sheet(wb, ws4, 'BG par titulaire');

  XLSX.writeFile(
    wb,
    `Balance générale - ${moment(new Date()).format('DD/MM/YYYY')}.xlsx`,
  );
};
